import { useEffect, useState, type FormEvent } from "react";
import type { List } from "@guestbook/shared";
import { Button } from "@/shared/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/Dialog";
import { FormField } from "@/shared/components/form/FormField";
import { TextareaFormField } from "@/shared/components/form/TextareaFormField";
import { useUpdateList } from "@/shared/lib/lists";

interface EditListDialogProps {
  list: List | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal form for renaming a list or updating its description. `list` is
 * nullable so the parent can render the dialog once and toggle which row
 * it edits.
 */
export function EditListDialog({ list, open, onOpenChange }: EditListDialogProps) {
  const update = useUpdateList(list?.id ?? "");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (open && list) {
      setName(list.name);
      setDescription(list.description ?? "");
      update.reset();
    }
  }, [open, list?.id]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!list) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    update.mutate(
      { name: trimmed, description: description.trim() || null },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit list</DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          <FormField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          <TextareaFormField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
          {update.error ? (
            <p className="mx-4 type-body-sm text-danger-strong">
              {update.error.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="submit" disabled={update.isPending || !name.trim()}>
              {update.isPending ? "Saving…" : "Save changes"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
